import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';
import ApiClient from '../../utils/apiClient.js';

/**
 * GlobalElevenLabsSettings component for admin dashboard
 * Manages global ElevenLabs settings that apply to all parrots
 */
const GlobalElevenLabsSettings = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const [apiClient] = useState(() => new ApiClient(getToken));

  // State management
  const [settings, setSettings] = useState({
    use_speaker_boost: true,
    language_model: 'claude-3-5-sonnet',
    voice_model_preference: 'Flash',
    max_tokens: -1,
    optimize_streaming_latency: '0'
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [changeHistory, setChangeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Available voice model preferences for ElevenLabs
  const voiceModelPreferences = [
    { value: 'Flash', label: 'Flash' },
    { value: 'Turbo', label: 'Turbo' }
  ];

  // Available optimize streaming latency options
  const optimizeLatencyOptions = [
    { value: '0', label: '0 - Highest Quality Highest Latency' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4 - Lowest Quality Lowest Latency' }
  ];

  // Available language models for conversation AI
  const languageModels = [
    { value: 'claude-3-5-sonnet', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-7-sonnet', label: 'Claude 3.7 Sonnet' },
    { value: 'claude-3-haiku', label: 'Claude 3 Haiku' },
    { value: 'claude-sonnet-4', label: 'Claude 4.0 Sonnet' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
    { value: 'gpt-3.5-turbo', label: 'GPT 3.5 Turbo' },
    { value: 'gpt-4', label: 'GPT 4' },
    { value: 'gpt-4-turbo', label: 'GPT 4 Turbo' },
    { value: 'gpt-4.1', label: 'GPT 4.1' },
    { value: 'gpt-4.1-mini', label: 'GPT 4.1 Mini' },
    { value: 'gpt-4.1-nano', label: 'GPT 4.1 Nano' },
    { value: 'gpt-4o', label: 'GPT 4o' },
    { value: 'gpt-4o-mini', label: 'GPT 4o Mini' },
    { value: 'grok-beta', label: 'Grok Beta' },
    { value: 'qwen3-4b', label: 'Qwen3 4b' },
    { value: 'watt-tool-8b', label: 'Watt Tool 8b' }
  ];

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  /**
   * Fetch current global settings from API
   */
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/admin/elevenlabs-settings');
      
      if (response.settings) {
        // Handle backward compatibility for legacy voice_model field
        let processedSettings = { ...response.settings };
        
        // If we have the old voice_model field but not the new preference field
        if (processedSettings.voice_model && !processedSettings.voice_model_preference) {
          processedSettings.voice_model_preference = getVoiceModelPreferenceDisplay(processedSettings.voice_model);
        }
        
        // Ensure we have a default preference if neither field exists
        if (!processedSettings.voice_model_preference) {
          processedSettings.voice_model_preference = 'Flash';
        }
        
        setSettings(processedSettings);
        setOriginalSettings(processedSettings);
      }
      
      if (response.history) {
        setChangeHistory(response.history);
      }
    } catch (error) {
      console.error('Failed to fetch global settings:', error);
      showToast('Failed to load global settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle input changes
   */
  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  /**
   * Validate settings against ElevenLabs API requirements
   */
  const validateSettings = () => {
    const errors = {};

    // Validate language model
    const validLanguageModels = languageModels.map(m => m.value);
    if (!validLanguageModels.includes(settings.language_model)) {
      errors.language_model = 'Please select a valid conversation AI model. This model will generate responses for all your parrots.';
    }

    // Validate voice model preference
    const validVoiceModelPreferences = voiceModelPreferences.map(p => p.value);
    if (!validVoiceModelPreferences.includes(settings.voice_model_preference)) {
      errors.voice_model_preference = 'Please select either Flash or Turbo. The system will automatically choose the correct ElevenLabs model based on each parrot\'s language.';
    }

    // Validate max tokens
    if (settings.max_tokens !== -1 && (settings.max_tokens < 1 || settings.max_tokens > 10000)) {
      errors.max_tokens = 'Max tokens must be -1 for unlimited responses, or between 1 and 10000 to limit response length. Lower values create shorter responses.';
    }

    // Validate optimize streaming latency
    const validLatencyValues = optimizeLatencyOptions.map(o => o.value);
    if (!validLatencyValues.includes(settings.optimize_streaming_latency)) {
      errors.optimize_streaming_latency = 'Please select a latency optimization level. This balances audio quality against response speed for phone calls.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Test settings configuration before applying
   */
  const handleTestSettings = async () => {
    if (!validateSettings()) {
      return;
    }

    setTesting(true);
    try {
      // Send only the core settings for testing
      const coreSettings = {
        use_speaker_boost: settings.use_speaker_boost,
        language_model: settings.language_model,
        voice_model_preference: settings.voice_model_preference,
        max_tokens: settings.max_tokens,
        optimize_streaming_latency: settings.optimize_streaming_latency
      };
      
      const response = await apiClient.post('/api/admin/elevenlabs-settings/test', coreSettings);
      
      if (response.valid) {
        showToast('Settings configuration is valid', 'success');
      } else {
        showToast(`Settings validation failed: ${response.error}`, 'error');
      }
    } catch (error) {
      console.error('Failed to test settings:', error);
      showToast('Failed to test settings configuration', 'error');
    } finally {
      setTesting(false);
    }
  };

  /**
   * Check if settings have changed
   */
  const hasChanges = () => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  };

  /**
   * Handle save settings
   */
  const handleSave = () => {
    if (!validateSettings()) {
      return;
    }

    if (!hasChanges()) {
      showToast('No changes to save', 'info');
      return;
    }

    setShowConfirmDialog(true);
  };

  /**
   * Confirm and save settings
   */
  const confirmSave = async () => {
    setSaving(true);
    setShowConfirmDialog(false);
    
    try {
      // Send only the core settings, not metadata
      const coreSettings = {
        use_speaker_boost: settings.use_speaker_boost,
        language_model: settings.language_model,
        voice_model_preference: settings.voice_model_preference,
        max_tokens: settings.max_tokens,
        optimize_streaming_latency: settings.optimize_streaming_latency
      };
      
      const response = await apiClient.put('/api/admin/elevenlabs-settings', coreSettings);
      
      if (response.settings) {
        setSettings(response.settings);
        setOriginalSettings(response.settings);
        showToast('Global settings updated successfully', 'success');
        
        // Refresh change history
        fetchSettings();
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      showToast(error.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Reset settings to original values
   */
  const handleReset = () => {
    setSettings({ ...originalSettings });
    setFormErrors({});
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  /**
   * Convert legacy voice model names to preference display
   */
  const getVoiceModelPreferenceDisplay = (voiceModel) => {
    // Handle new preference format
    if (voiceModel === 'Flash' || voiceModel === 'Turbo') {
      return voiceModel;
    }
    
    // Handle legacy model names for backward compatibility
    if (voiceModel === 'eleven_flash_v2' || voiceModel === 'eleven_flash_v2_5') {
      return 'Flash';
    }
    if (voiceModel === 'eleven_turbo_v2' || voiceModel === 'eleven_turbo_v2_5') {
      return 'Turbo';
    }
    
    // Default fallback
    return voiceModel || 'Flash';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Global ElevenLabs Settings
          </h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Configure global ElevenLabs settings that apply to all parrots
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowHistoryModal(true)}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              theme === 'dark'
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            View History
          </button>
          <button
            onClick={handleTestSettings}
            disabled={testing || !hasChanges()}
            className={`px-4 py-2 rounded-lg transition-colors ${
              testing || !hasChanges()
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {testing ? 'Testing...' : 'Test Settings'}
          </button>
        </div>
      </div>

      {/* Settings Form */}
      <div className={`${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } rounded-lg border shadow-sm`}>
        <div className="p-6 space-y-6">
          {/* Use Speaker Boost */}
          <div>
            <label className={`block text-sm font-medium ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            } mb-2`}>
              Use Speaker Boost
            </label>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleInputChange('use_speaker_boost', true)}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  settings.use_speaker_boost
                    ? 'bg-teal-600 text-white border-teal-600'
                    : theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Enabled
              </button>
              <button
                onClick={() => handleInputChange('use_speaker_boost', false)}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  !settings.use_speaker_boost
                    ? 'bg-teal-600 text-white border-teal-600'
                    : theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Disabled
              </button>
            </div>
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Enhances voice clarity and volume for better audio quality
            </p>
          </div>

          {/* Language Model */}
          <div>
            <label className={`block text-sm font-medium ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            } mb-2`}>
              Language Model
            </label>
            <select
              value={settings.language_model}
              onChange={(e) => handleInputChange('language_model', e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } ${formErrors.language_model ? 'border-red-500' : ''}`}
            >
              {languageModels.map(model => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
            {formErrors.language_model && (
              <p className="mt-1 text-sm text-red-600">{formErrors.language_model}</p>
            )}
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              The AI model used for generating conversation responses
            </p>
          </div>

          {/* Voice Model Preference */}
          <div>
            <label className={`block text-sm font-medium ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            } mb-2`}>
              Voice Model Preference
            </label>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleInputChange('voice_model_preference', 'Flash')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  settings.voice_model_preference === 'Flash'
                    ? 'bg-teal-600 text-white border-teal-600'
                    : theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Flash
              </button>
              <button
                onClick={() => handleInputChange('voice_model_preference', 'Turbo')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  settings.voice_model_preference === 'Turbo'
                    ? 'bg-teal-600 text-white border-teal-600'
                    : theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Turbo
              </button>
            </div>
            {formErrors.voice_model_preference && (
              <p className="mt-1 text-sm text-red-600">{formErrors.voice_model_preference}</p>
            )}
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Voice model preference that automatically selects the appropriate ElevenLabs model based on each parrot's language setting. Flash provides faster response times, while Turbo offers enhanced quality. The system automatically uses v2 models for English and v2_5 models for other languages.
            </p>
          </div>

          {/* Optimize Streaming Latency */}
          <div>
            <label className={`block text-sm font-medium ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            } mb-2`}>
              Optimize Streaming Latency
            </label>
            <select
              value={settings.optimize_streaming_latency}
              onChange={(e) => handleInputChange('optimize_streaming_latency', e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } ${formErrors.optimize_streaming_latency ? 'border-red-500' : ''}`}
            >
              {optimizeLatencyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {formErrors.optimize_streaming_latency && (
              <p className="mt-1 text-sm text-red-600">{formErrors.optimize_streaming_latency}</p>
            )}
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Controls the balance between audio quality and streaming latency
            </p>
          </div>

          {/* Max Tokens */}
          <div>
            <label className={`block text-sm font-medium ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            } mb-2`}>
              Max Tokens
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                value={settings.max_tokens}
                onChange={(e) => handleInputChange('max_tokens', parseInt(e.target.value) || -1)}
                min="-1"
                max="10000"
                className={`flex-1 px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } ${formErrors.max_tokens ? 'border-red-500' : ''}`}
              />
              <button
                onClick={() => handleInputChange('max_tokens', -1)}
                className={`px-3 py-2 rounded-lg border transition-colors ${
                  settings.max_tokens === -1
                    ? 'bg-teal-600 text-white border-teal-600'
                    : theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Unlimited
              </button>
            </div>
            {formErrors.max_tokens && (
              <p className="mt-1 text-sm text-red-600">{formErrors.max_tokens}</p>
            )}
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Maximum number of tokens per response (-1 for unlimited, 1-10000 for limited)
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={`px-6 py-4 border-t ${
          theme === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
        } rounded-b-lg flex justify-between`}>
          <button
            onClick={handleReset}
            disabled={!hasChanges()}
            className={`px-4 py-2 rounded-lg transition-colors ${
              !hasChanges()
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : theme === 'dark'
                ? 'bg-gray-600 text-white hover:bg-gray-500'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            Reset Changes
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges()}
            className={`px-6 py-2 rounded-lg transition-colors ${
              saving || !hasChanges()
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          } rounded-lg p-6 max-w-md w-full mx-4`}>
            <h3 className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}>
              Confirm Global Settings Update
            </h3>
            <div className={`${
              theme === 'dark' ? 'bg-yellow-900 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
            } border rounded-lg p-4 mb-4`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-yellow-200' : 'text-yellow-800'
                  }`}>
                    Impact Warning
                  </h4>
                  <p className={`mt-1 text-sm ${
                    theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'
                  }`}>
                    These settings will apply to all existing and new parrots. The voice model preference will automatically select the appropriate ElevenLabs model based on each parrot's language setting (v2 models for English, v2_5 models for other languages). Existing parrots will be updated automatically in the background.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmSave}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors dark:text-white"
              >
                Confirm Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          } rounded-lg max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden`}>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className={`text-lg font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Settings Change History
                </h3>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className={`p-2 rounded-lg transition-colors ${
                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {changeHistory.length === 0 ? (
                <p className={`text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  No change history available
                </p>
              ) : (
                <div className="space-y-4">
                  {changeHistory.map((change, index) => (
                    <div key={index} className={`${
                      theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    } border rounded-lg p-4`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-sm font-medium ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          Updated by: {change.updated_by}
                        </span>
                        <span className={`text-xs ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {formatDate(change.updated_at)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className={`font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            Speaker Boost:
                          </span>
                          <span className={`ml-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {change.use_speaker_boost ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div>
                          <span className={`font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            Language Model:
                          </span>
                          <span className={`ml-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {change.language_model}
                          </span>
                        </div>
                        <div>
                          <span className={`font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            Voice Model Preference:
                          </span>
                          <span className={`ml-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {getVoiceModelPreferenceDisplay(change.voice_model_preference || change.voice_model)}
                          </span>
                        </div>
                        <div>
                          <span className={`font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            Max Tokens:
                          </span>
                          <span className={`ml-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {change.max_tokens === -1 ? 'Unlimited' : change.max_tokens}
                          </span>
                        </div>
                        <div>
                          <span className={`font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            Optimize Latency:
                          </span>
                          <span className={`ml-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {change.optimize_streaming_latency || '0'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalElevenLabsSettings;
